import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Home, Heart, Shield, ScrollText } from "lucide-react";

export default function Footer() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Message sent. We'll get back to you soon!");
            setForm({ name: "", email: "", subject: "", message: "" });
            setOpen(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer className="mt-20 border-t border-border/60 bg-background/60" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
                                <Home className="w-4 h-4" strokeWidth={2.5} />
                            </div>
                            <span className="font-display text-lg font-bold tracking-tight">
                                Erasmus<span className="text-primary">Swap</span>
                            </span>
                        </div>
                        <p className="mt-3 text-sm text-muted-foreground max-w-xs">
                            Room swaps for Erasmus and exchange students across the world. 100% free.
                        </p>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Explore</div>
                        <ul className="mt-3 space-y-2 text-sm">
                            <li><Link to="/browse" className="text-foreground/80 hover:text-primary" data-testid="footer-browse-link">Browse rooms</Link></li>
                            <li><Link to="/register" className="text-foreground/80 hover:text-primary" data-testid="footer-register-link">Create an account</Link></li>
                            <li><Link to="/login" className="text-foreground/80 hover:text-primary" data-testid="footer-login-link">Log in</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Support</div>
                        <ul className="mt-3 space-y-2 text-sm">
                            <li>
                                <button
                                    type="button"
                                    onClick={() => setOpen(true)}
                                    data-testid="footer-contact-btn"
                                    className="text-foreground/80 hover:text-primary inline-flex items-center gap-1.5"
                                >
                                    <Mail className="w-3.5 h-3.5" /> Contact us
                                </button>
                            </li>
                            <li>
                                <Link
                                    to="/privacy"
                                    data-testid="footer-privacy-link"
                                    className="text-foreground/80 hover:text-primary inline-flex items-center gap-1.5"
                                >
                                    <Shield className="w-3.5 h-3.5" /> Privacy Policy
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/terms"
                                    data-testid="footer-terms-link"
                                    className="text-foreground/80 hover:text-primary inline-flex items-center gap-1.5"
                                >
                                    <ScrollText className="w-3.5 h-3.5" /> Terms of Use
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="mt-10 pt-6 border-t border-border/40 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div>© {new Date().getFullYear()} ErasmusSwap</div>
                    <div className="flex items-center gap-1">Made with <Heart className="w-3 h-3 text-primary fill-primary" /> for exchange students</div>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="contact-dialog" className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Contact us</DialogTitle>
                        <DialogDescription>
                            Questions, feedback, or a problem to report? Send us a message and we'll reply by email.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4 mt-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="c-name">Your name</Label>
                                <Input
                                    id="c-name" required data-testid="contact-name-input"
                                    className="mt-1.5 rounded-xl"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="c-email">Your email</Label>
                                <Input
                                    id="c-email" type="email" required data-testid="contact-email-input"
                                    className="mt-1.5 rounded-xl"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="c-subject">Subject (optional)</Label>
                            <Input
                                id="c-subject" data-testid="contact-subject-input"
                                className="mt-1.5 rounded-xl"
                                value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label htmlFor="c-message">Message</Label>
                            <Textarea
                                id="c-message" required data-testid="contact-message-input"
                                rows={5}
                                className="mt-1.5 rounded-xl"
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                            />
                        </div>
                        <Button
                            type="submit" disabled={submitting}
                            data-testid="contact-submit-btn"
                            className="w-full rounded-full bg-primary hover:bg-primary/90"
                        >
                            {submitting ? "Sending…" : "Send message"}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </footer>
    );
}
